'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'

/**
 * GTM Event Tracker Component
 * Tracks all key conversion events and user interactions
 * GTM ID: GTM-NZPPZSDM
 */

// Declare dataLayer globally
declare global {
  interface Window {
    dataLayer: any[]
  }
}

export function GTMTracker() {
  const pathname = usePathname()

  useEffect(() => {
    // Track page view
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: pathname,
        page_title: document.title,
        timestamp: new Date().toISOString(),
      })
    }
  }, [pathname])

  useEffect(() => {
    // Track WhatsApp clicks
    const trackWhatsAppClicks = () => {
      const whatsappLinks = document.querySelectorAll('a[href*="wa.me"], a[href*="whatsapp"]')
      whatsappLinks.forEach((link) => {
        link.addEventListener('click', () => {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'whatsapp_click',
              whatsapp_source: link.getAttribute('href'),
              page_path: pathname,
              timestamp: new Date().toISOString(),
            })
          }
        })
      })
    }

    // Track phone call clicks
    const trackPhoneClicks = () => {
      const phoneLinks = document.querySelectorAll('a[href^="tel:"]')
      phoneLinks.forEach((link) => {
        link.addEventListener('click', () => {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'phone_click',
              phone_number: link.getAttribute('href'),
              phone_text: link.textContent,
              page_path: pathname,
              timestamp: new Date().toISOString(),
            })
          }
        })
      })
    }

    // Track email clicks
    const trackEmailClicks = () => {
      const emailLinks = document.querySelectorAll('a[href^="mailto:"]')
      emailLinks.forEach((link) => {
        link.addEventListener('click', () => {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'email_click',
              email: link.getAttribute('href'),
              page_path: pathname,
              timestamp: new Date().toISOString(),
            })
          }
        })
      })
    }

    // Track form interactions
    const trackFormEvents = () => {
      const forms = document.querySelectorAll('form')
      forms.forEach((form) => {
        // Track form focus
        form.addEventListener('focusin', (e) => {
          const target = e.target as HTMLInputElement
          if (target.name) {
            if (window.dataLayer) {
              window.dataLayer.push({
                event: 'form_field_focus',
                form_name: form.name || form.id || 'unnamed_form',
                field_name: target.name,
                field_type: target.type,
                page_path: pathname,
                timestamp: new Date().toISOString(),
              })
            }
          }
        })

        // Track form submission
        form.addEventListener('submit', (e) => {
          const formData = new FormData(form as HTMLFormElement)
          const fields = Array.from(formData.keys())
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'form_submission',
              form_name: form.name || form.id || 'unnamed_form',
              form_fields: fields,
              page_path: pathname,
              timestamp: new Date().toISOString(),
            })
          }
        })

        // Track form field changes
        const inputs = form.querySelectorAll('input, textarea, select')
        inputs.forEach((input) => {
          input.addEventListener('change', () => {
            if (window.dataLayer) {
              window.dataLayer.push({
                event: 'form_field_change',
                form_name: form.name || form.id || 'unnamed_form',
                field_name: (input as HTMLInputElement).name,
                field_type: (input as HTMLInputElement).type,
                page_path: pathname,
                timestamp: new Date().toISOString(),
              })
            }
          })
        })
      })
    }

    // Track CTA button clicks
    const trackCTAClicks = () => {
      const ctaButtons = document.querySelectorAll('[data-cta], button, a[role="button"]')
      ctaButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'cta_click',
              cta_text: button.textContent?.trim(),
              cta_id: button.id,
              cta_class: button.className,
              page_path: pathname,
              timestamp: new Date().toISOString(),
            })
          }
        })
      })
    }

    // Track scroll depth
    let maxScrollPercentage = 0
    const trackScrollDepth = () => {
      window.addEventListener('scroll', () => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
        const scrolled = (window.scrollY / scrollHeight) * 100
        const roundedScroll = Math.round(scrolled / 10) * 10

        if (roundedScroll > maxScrollPercentage) {
          maxScrollPercentage = roundedScroll
          if (window.dataLayer && roundedScroll > 0 && roundedScroll % 25 === 0) {
            window.dataLayer.push({
              event: 'scroll_depth',
              scroll_depth: roundedScroll,
              page_path: pathname,
              timestamp: new Date().toISOString(),
            })
          }
        }
      })
    }

    // Track external link clicks
    const trackExternalLinks = () => {
      const links = document.querySelectorAll('a[target="_blank"]')
      links.forEach((link) => {
        link.addEventListener('click', () => {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'external_link_click',
              link_url: link.getAttribute('href'),
              link_text: link.textContent?.trim(),
              page_path: pathname,
              timestamp: new Date().toISOString(),
            })
          }
        })
      })
    }

    // Track time on page (every 30 seconds)
    const startTime = Date.now()
    const timeInterval = setInterval(() => {
      const timeOnPage = Math.round((Date.now() - startTime) / 1000)
      if (window.dataLayer && timeOnPage > 0 && timeOnPage % 30 === 0) {
        window.dataLayer.push({
          event: 'time_on_page',
          time_spent: timeOnPage,
          page_path: pathname,
          timestamp: new Date().toISOString(),
        })
      }
    }, 30000)

    // Track video plays (if any)
    const trackVideoPlays = () => {
      const videos = document.querySelectorAll('video, iframe[src*="youtube"], iframe[src*="vimeo"]')
      videos.forEach((video) => {
        if (video.tagName === 'VIDEO') {
          video.addEventListener('play', () => {
            if (window.dataLayer) {
              window.dataLayer.push({
                event: 'video_play',
                video_title: video.title || 'Unnamed video',
                page_path: pathname,
                timestamp: new Date().toISOString(),
              })
            }
          })
        } else if (video.tagName === 'IFRAME') {
          video.addEventListener('load', () => {
            if (window.dataLayer) {
              window.dataLayer.push({
                event: 'video_loaded',
                video_source: video.getAttribute('src'),
                page_path: pathname,
                timestamp: new Date().toISOString(),
              })
            }
          })
        }
      })
    }

    // Initialize all tracking
    setTimeout(() => {
      trackWhatsAppClicks()
      trackPhoneClicks()
      trackEmailClicks()
      trackFormEvents()
      trackCTAClicks()
      trackScrollDepth()
      trackExternalLinks()
      trackVideoPlays()
    }, 500) // Wait for DOM to be fully ready

    return () => {
      clearInterval(timeInterval)
    }
  }, [pathname])

  return null // This component doesn't render anything
}

/**
 * Helper function to manually push events to GTM
 * Usage: pushGTMEvent('custom_event', { key: 'value' })
 */
export function pushGTMEvent(eventName: string, eventData: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData,
      timestamp: new Date().toISOString(),
    })
  }
}
